import {
  getRouteTrackingEnvironment,
  routeTrackingConfig,
  RouteTrackingRule,
  RouteTrackingTarget,
} from "app/config/route-tracking-config";
import { Path } from "app/constants/paths";
import { configureXPixel } from "app/utils/x-pixel";
import { useEffect } from "react";
import { matchPath, useLocation } from "react-router-dom";

type TrackingPage =
  | {
      type: "ido";
      id: string;
    }
  | {
      type: "quest";
      id: string;
    }
  | {
      type: "build_karma";
      id: string;
    };

const firedRoutePixels = new Set<string>();

const normalizeTrackingId = (value: string): string => {
  const trimmedValue = value.trim();

  try {
    return decodeURIComponent(trimmedValue);
  } catch (error) {
    return trimmedValue;
  }
};

const isValidDate = (date: Date): boolean => !isNaN(date.getTime());

const warnInvalidTrackingRule = (rule: RouteTrackingRule, field: string) => {
  if (process.env.NODE_ENV === "production") {
    return;
  }

  // eslint-disable-next-line no-console
  console.warn(`Route tracking rule "${rule.id}" has invalid ${field}`);
};

const isRuleInActiveWindow = (rule: RouteTrackingRule, now: Date): boolean => {
  const activeUntil = new Date(rule.activeUntil);

  if (!isValidDate(activeUntil)) {
    warnInvalidTrackingRule(rule, "activeUntil");
    return false;
  }

  if (now > activeUntil) {
    return false;
  }

  if (!rule.activeFrom) {
    return true;
  }

  const activeFrom = new Date(rule.activeFrom);

  if (!isValidDate(activeFrom)) {
    warnInvalidTrackingRule(rule, "activeFrom");
    return false;
  }

  return now >= activeFrom;
};

const matchesTarget = (
  target: RouteTrackingTarget,
  page: TrackingPage
): boolean => {
  if (target.type === "build_karma") {
    return page.type === "build_karma" || page.type === "quest";
  }

  return (
    target.type === page.type &&
    target.ids.some(
      (id) => normalizeTrackingId(id) === normalizeTrackingId(page.id)
    )
  );
};

const getTrackingPage = (
  pathname: string,
  search: string
): TrackingPage | null => {
  const idoMatch = matchPath<{ idoId: string }>(pathname, {
    path: Path.IDO,
    exact: true,
  });

  if (idoMatch?.params.idoId) {
    return {
      type: "ido",
      id: idoMatch.params.idoId,
    };
  }

  const questMatch =
    matchPath(pathname, { path: Path.BUILD_KARMA, exact: true }) ||
    matchPath(pathname, { path: Path.BUILD_KARMA_SLASH, exact: true });

  if (!questMatch) {
    return null;
  }

  const questId = new URLSearchParams(search).get("questId");

  if (!questId) {
    return {
      type: "build_karma",
      id: "build_karma",
    };
  }

  return {
    type: "quest",
    id: questId,
  };
};

const getActiveTrackingRules = (
  page: TrackingPage | null,
  now: Date
): RouteTrackingRule[] => {
  if (!page) {
    return [];
  }

  const environment = getRouteTrackingEnvironment();

  return routeTrackingConfig[environment].filter(
    (rule) =>
      rule.enabled &&
      isRuleInActiveWindow(rule, now) &&
      rule.targets.some((target) => matchesTarget(target, page))
  );
};

const getRouteKey = (
  location: ReturnType<typeof useLocation>,
  page: TrackingPage
): string => {
  const locationKey = location.key || `${location.pathname}${location.search}`;

  return `${locationKey}:${page.type}:${page.id}`;
};

export const useRouteTracking = () => {
  const location = useLocation();

  useEffect(() => {
    const page = getTrackingPage(location.pathname, location.search);
    const rules = getActiveTrackingRules(page, new Date());

    if (!page || rules.length === 0) {
      return;
    }

    const routeKey = getRouteKey(location, page);

    rules.forEach((rule) => {
      const firedKey = `${rule.id}:${rule.provider}:${rule.pixelId}:${routeKey}`;

      if (firedRoutePixels.has(firedKey)) {
        return;
      }

      firedRoutePixels.add(firedKey);

      if (rule.provider === "x") {
        configureXPixel(rule.pixelId);
      }
    });
  }, [location]);
};

export default useRouteTracking;
